// src/pages/ScaleComponents/emailtransporter.js
//
// Privacy-safe email transporter:
// - ALWAYS sends one message per recipient (no shared To/CC/BCC).
// - Preserves your placeholder merge, templates, and logging.
// - Backward compatible with single-recipient transactional senders.

import { db } from '../../services/firebase';
import { fetchServerRecipientsRowsForList } from '../../contactManager/listContactsService';
import {
    EMAIL_LOGO_URL as LOGO_URL,
    EMAIL_LOGO_HEADER_GRADIENT,
    wrapTransactionalEmailHtml,
    emailCartPreBlockStyleAttr,
    emailCartCtaButtonMergeHtml,
    getWholesaleCartUrl,
} from '../../utils/emailBrandStyles';
import { applyCartNudgeTestRouting } from '../../utils/cartNudgeEmailGuard';
import {
    replaceEmailPlaceholdersWithUsage,
    replaceEmailPlaceholdersFully,
    extractRemainingMergeTokensAfterMerge,
} from '../../utils/emailPlaceholderMerge';
import { applyCartNudgeTemplateHtmlTransforms } from '../../utils/cartNudgeTemplateHtml';
import { extractAdditionalEmailRowsFromUserProfileData } from '../../utils/accountAdditionalEmailsUtils';
import { stripOrderNumberHash } from '../../utils/orderDisplayUtils';
import {
    normalizeOrderLinesForPackedEmail,
    buildOrderPackedItemsHtml,
    buildOrderPackedItemsPlainText,
    buildChilledProductNoteHtml,
    buildChilledProductNotePlainText,
    getShippingMethodLabelFromOrder,
    defaultOrderPackedTemplateHtml,
    defaultOrderPackedTemplateText,
} from '../../utils/orderPackedEmailHelpers';
import { isPackersCustomerPackEmailsMuted } from '../../utils/packersCustomerPackEmailSession';
import {
    doc,
    getDoc,
    collection,
    getDocs,
    addDoc,
    serverTimestamp,
    query,
    where,
    limit,
} from 'firebase/firestore';

/**
 * Outbound email endpoint (Cloud Run, HTTPS).
 * Overridable via env for staging or local development.
 */
const ENDPOINT =
    process.env.REACT_APP_ORDER_TRANSPORTER_ENDPOINT ||
    'https://newslettertransporter-844411985132.europe-west1.run.app/send-email';

/**
 * Test-mode: force all mail to TEST_ADDRESS.
 * Useful for staging UIs that still point at production data.
 */
const TEST_MODE = String(process.env.REACT_APP_EMAIL_TEST_MODE).toLowerCase() === 'true';
const TEST_ADDRESS =
    process.env.REACT_APP_EMAIL_TEST_ADDRESS ||
    'digital@londonsmokeandcure.co.uk';

const TEMPLATE_COLL = 'email_templates';
const LOGS_COLL = 'email_logs';

const TEMPLATE_KEYS = {
    ORDER_DELIVERED: 'order_delivered',
    INVOICE_AVAILABLE: 'invoice_available',
    ORDER_PACKED: 'order_packed',
    ORDER_SHIPPED: 'order_shipped',
    ORDER_CONFIRMATION: 'order_confirmation',
    CART_CUTOFF_REMINDER: 'cart_cutoff_reminder',
    /** Contact Manager → Promote to account (welcome + login). Override via Firestore `email_templates/account_welcome_promote`. */
    ACCOUNT_WELCOME_PROMOTE: 'account_welcome_promote',
    /** Invite customers to add extra account emails — Newsletter + Template Studio. */
    ADDITIONAL_EMAILS_INVITE: 'additional_emails_invite',
};

/* ────────────────────────── Utilities ────────────────────────── */

/** @returns {{ content: string, placeholderUsage: object }} — content is fully merged (regex + literal `--key--` pass). */
function replacePlaceholders(content, data) {
    const usage = replaceEmailPlaceholdersWithUsage(content, data);
    const full = replaceEmailPlaceholdersFully(content, data);
    return { content: full, placeholderUsage: usage.placeholderUsage };
}

/** Normalize to trimmed, lower-cased email string or '' */
function cleanEmail(v) {
    return String(v || '').trim();
}

/** Normalize an input into an array of distinct, truthy emails (case-insensitive dedupe). */
function normalizeToArray(to) {
    console.log(`[emailtransporter] normalizeToArray called with:`, to, `(type: ${typeof to}, isArray: ${Array.isArray(to)})`);
    const arr = Array.isArray(to) ? to : [to];
    const seen = new Set();
    const out = [];
    for (const raw of arr) {
        console.log(`[emailtransporter] Processing raw value:`, raw, `(type: ${typeof raw})`);
        const em = cleanEmail(raw);
        console.log(`[emailtransporter] After cleanEmail:`, em, `(type: ${typeof em}, length: ${em ? em.length : 'N/A'}, truthy: ${!!em})`);
        if (!em) {
            console.log(`[emailtransporter] Skipping empty email`);
            continue;
        }
        const key = em.toLowerCase();
        if (seen.has(key)) {
            console.log(`[emailtransporter] Skipping duplicate:`, key);
            continue;
        }
        seen.add(key);
        out.push(em);
        console.log(`[emailtransporter] Added email to output:`, em);
    }
    console.log(`[emailtransporter] normalizeToArray returning:`, out, `(length: ${out.length})`);
    return out;
}

/**
 * Merge both the recipient row data (from lists) AND the event info payload.
 * Provides several friendly aliases to simplify template authoring.
 */
function buildMergeData(info, recipientData) {
    const out = { ...(recipientData || {}) };

    // Map common recipient aliases → canonical keys
    if (recipientData) {
        const em = recipientData.Email || recipientData.email;
        if (em) {
            out.Email = em;
            out.email = em;
        }

        const fn =
            recipientData['First Name'] ||
            recipientData.firstName ||
            recipientData.first;
        if (fn) {
            out['First Name'] = fn;
            out.firstName = fn;
        }

        const ln =
            recipientData['Last Name'] ||
            recipientData.lastName ||
            recipientData.last;
        if (ln) {
            out['Last Name'] = ln;
            out.lastName = ln;
        }

        const comp =
            recipientData['Company Name'] ||
            recipientData.company ||
            recipientData.Company;
        if (comp) {
            out['Company Name'] = comp;
            out.company = comp;
        }
    }

    // Event info keys — do not overwrite list/recipient fields with empty strings (mail-merge safety).
    if (info && typeof info === 'object') {
        Object.entries(info).forEach(([k, v]) => {
            if (v != null && v !== '') out[k] = v;
        });

        // Friendly extras for delivered/shipped geography
        if (info.location && typeof info.location === 'object') {
            out.locationLat = info.location.lat;
            out.locationLng = info.location.lng;
        }
        // Invoice link convenience
        if (info.pdfDownloadUrl && !out.invoiceLink) out.invoiceLink = info.pdfDownloadUrl;
        if (!out.sentAt) out.sentAt = new Date().toLocaleString('en-GB');
    }

    return out;
}

/**
 * Fetch first matching recipient row (by email) from any list in `lists` collection.
 */
async function getRecipientDataFromLists(email) {
    if (!email) return null;
    const listsSnap = await getDocs(collection(db, 'lists'));
    for (const listDoc of listsSnap.docs) {
        const list = listDoc.data();
        // eslint-disable-next-line no-await-in-loop
        const rows = await fetchServerRecipientsRowsForList(listDoc.id, list);
        for (const rec of rows) {
            const em = (rec.Email || rec.email || '').toLowerCase();
            if (em && em === String(email).toLowerCase()) return rec;
        }
    }
    return null;
}

/**
 * Get all lists that contain a specific email address
 */
async function getListMembershipForEmail(email) {
    if (!email) return [];
    const emailLower = String(email).toLowerCase();
    const listsSnap = await getDocs(collection(db, 'lists'));
    const membership = [];
    
    for (const listDoc of listsSnap.docs) {
        const list = listDoc.data();
        // eslint-disable-next-line no-await-in-loop
        const rows = await fetchServerRecipientsRowsForList(listDoc.id, list);
        for (const rec of rows) {
            const em = (rec.Email || rec.email || '').toLowerCase();
            if (em === emailLower) {
                membership.push({
                    listId: listDoc.id,
                    listName: list.name || listDoc.id,
                    addedAt: rec.addedAt || list.updatedAt || list.createdAt || null,
                    source: rec.source || 'unknown'
                });
                break; // Found in this list, move to next list
            }
        }
    }
    
    return membership;
}

/**
 * Resolve `users/{id}` where login email or username matches (case variants).
 */
async function findUserDocSnapByAccountEmail(rawEmail) {
    const e = cleanEmail(rawEmail);
    if (!e) return null;
    const variants = Array.from(new Set([e, e.toLowerCase()]));
    for (const cand of variants) {
        const qy = query(collection(db, 'users'), where('email', '==', cand), limit(1));
        const snap = await getDocs(qy);
        if (!snap.empty) return snap.docs[0];
    }
    const qUser = query(collection(db, 'users'), where('username', '==', e.toLowerCase()), limit(1));
    const snapUser = await getDocs(qUser);
    if (!snapUser.empty) return snapUser.docs[0];
    return null;
}

/**
 * For each recipient that is a primary account email, append that account's additional emails
 * (one send per address; privacy-safe). Returns per-address merge hints so greetings use the
 * additional contact's name while order/body `info` still supplies line items and totals.
 */
async function expandRecipientsWithLinkedAdditionalEmails(recipientEmails) {
    const ordered = [...recipientEmails];
    const seen = new Set(recipientEmails.map((x) => cleanEmail(x).toLowerCase()).filter(Boolean));
    /** @type {Record<string, Object>} */
    const identityOverlayByEmail = {};

    for (const em of recipientEmails) {
        const userSnap = await findUserDocSnapByAccountEmail(em);
        if (!userSnap) continue;
        const userData = userSnap.data() || {};
        const extras = extractAdditionalEmailRowsFromUserProfileData(userData);
        const company =
            userData.companyName || userData.company || userData['Company Name'] || '';

        for (const ex of extras) {
            const addr = cleanEmail(ex.email);
            if (!addr) continue;
            const key = addr.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            ordered.push(addr);
            identityOverlayByEmail[key] = {
                Email: addr,
                email: addr,
                'First Name': ex.firstName,
                'Last Name': ex.lastName,
                firstName: ex.firstName,
                lastName: ex.lastName,
                'Company Name': company,
                company,
            };
        }
    }

    return { expandedRecipients: ordered, identityOverlayByEmail };
}

/**
 * Merge addresses snapshotted on the order at checkout (`accountAdditionalEmailCopies`).
 * Fills gaps when staff cannot read `users/{uid}` (Firestore rules).
 */
function mergeAccountEmailCopiesFromOrderSnapshot(
    expandedRecipients,
    identityOverlayByEmail,
    copyRows,
    companyName
) {
    if (!Array.isArray(copyRows) || copyRows.length === 0) {
        return { expandedRecipients, identityOverlayByEmail };
    }
    const ordered = [...expandedRecipients];
    const overlays = { ...identityOverlayByEmail };
    const seen = new Set(ordered.map((x) => cleanEmail(x).toLowerCase()).filter(Boolean));
    const comp = String(companyName || '').trim();

    for (const ex of copyRows) {
        const addr = cleanEmail(ex?.email);
        if (!addr) continue;
        const key = addr.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        ordered.push(addr);
        overlays[key] = {
            Email: addr,
            email: addr,
            'First Name': String(ex.firstName || '').trim(),
            'Last Name': String(ex.lastName || '').trim(),
            firstName: String(ex.firstName || '').trim(),
            lastName: String(ex.lastName || '').trim(),
            'Company Name': comp,
            company: comp,
        };
    }
    return { expandedRecipients: ordered, identityOverlayByEmail: overlays };
}

/** @returns {Promise<Array<{email:string,firstName?:string,lastName?:string}>>} */
/** Load `orders/{id}` for packed-email merge (line items, shipping method). Tries raw id and stripped #. */
async function loadOrderDataForPackedEmail(orderId) {
    if (!orderId) return null;
    const raw = String(orderId).trim();
    const candidates = [...new Set([raw, stripOrderNumberHash(raw)].filter(Boolean))];
    for (const id of candidates) {
        try {
            const snap = await getDoc(doc(db, 'orders', id));
            if (snap.exists()) return snap.data();
        } catch {
            /* try next */
        }
    }
    return null;
}

async function loadAccountEmailCopiesFromOrderById(orderId) {
    if (!orderId) return [];
    try {
        const od = await getDoc(doc(db, 'orders', String(orderId)));
        if (!od.exists()) return [];
        const d = od.data();
        return Array.isArray(d.accountAdditionalEmailCopies) ? d.accountAdditionalEmailCopies : [];
    } catch {
        return [];
    }
}

/**
 * After global `info` merge, force greeting / identity fields for linked additional recipients.
 */
function applyAccountAdditionalRecipientIdentity(data, overlay) {
    if (!overlay || !data) return data;
    const next = { ...data };
    const keys = ['firstName', 'First Name', 'lastName', 'Last Name', 'Email', 'email'];
    for (const k of keys) {
        const v = overlay[k];
        if (v != null && String(v).trim() !== '') next[k] = v;
    }
    return next;
}

/**
 * Create a compact summary of the payload for logs (avoid storing large HTML bodies).
 */
function summarizeEmailPayload(payload) {
    return {
        to: Array.isArray(payload?.to) ? payload.to : [payload?.to].filter(Boolean),
        subject: payload?.subject || '',
        textLen: (payload?.text || '').length,
        htmlLen: (payload?.html || '').length,
    };
}

/**
 * Write an entry to the `email_logs` collection.
 * Safe: swallows errors so logging never blocks email sending.
 */
async function logEmail(entry) {
    try {
        const nowIso = new Date().toISOString();
        await addDoc(collection(db, LOGS_COLL), {
            ...entry,
            createdAt: serverTimestamp(),
            createdAtText: nowIso, // used only as a tooltip/title; UI primarily shows createdAt
        });
    } catch (err) {
        // Non-fatal: still surface to console for debugging
        // eslint-disable-next-line no-console
        console.warn('[emailtransporter] failed to log email:', err);
    }
}

/* ────────────────────────── Templates ────────────────────────── */

/**
 * Legacy Firestore `email_templates/order_packed` docs sometimes omit `--itemsHtml--` / `--orderId--`.
 * Per-field fallback keeps production sends aligned with the app default without deleting Firestore docs.
 */
export function mergeOrderPackedTemplateWithFallback(stored, fallback) {
    if (!stored || !fallback) return stored || fallback;
    const h = String(stored.html ?? '');
    const s = String(stored.subject ?? '');
    const tx = String(stored.text ?? '');
    const htmlCritical = ['--itemsHtml--', '--customerName--', '--orderId--'];
    const badHtml = htmlCritical.some((ph) => !h.includes(ph));
    const html = badHtml ? fallback.html : stored.html;
    const subject = !s.includes('--orderId--') ? fallback.subject : stored.subject;
    const text = !tx.includes('--itemsText--') ? fallback.text : stored.text;
    return {
        ...stored,
        subject: subject ?? fallback.subject,
        text: text ?? fallback.text,
        html: html ?? fallback.html,
    };
}

async function getTemplateOrDefault(key, fallback) {
    try {
        const d = await getDoc(doc(db, TEMPLATE_COLL, key));
        if (d.exists()) {
            const t = d.data();
            let merged = {
                subject: t.subject ?? fallback.subject,
                text: t.text ?? fallback.text,
                html: t.html ?? fallback.html,
            };
            if (key === TEMPLATE_KEYS.ORDER_PACKED) {
                merged = mergeOrderPackedTemplateWithFallback(merged, fallback);
            }
            return merged;
        }
    } catch {
        // ignore and return fallback
    }
    return fallback;
}

/* ────────────────────────── Core Sender ────────────────────────── */

/**
 * sendMailMergeEmail (PRIVACY-SAFE)
 *
 * Sends email(s) with placeholder merge from `info` and the recipient's list row.
 * ALWAYS fans out to one message per recipient to prevent recipients seeing each other.
 *
 * @param {Object} params
 * @param {string|string[]} params.to - recipient(s)
 * @param {string} params.subject
 * @param {string} [params.text]
 * @param {string} [params.html]
 * @param {Object} [params.info] - event/context data for merge
 * @param {string} [params.templateKey] - one of TEMPLATE_KEYS (for filtering in logs)
 * @param {string} [params.sourcePath] - code path for debugging (shown in logs)
 * @param {Object} [params.context] - small object (e.g., { orderId, userId })
 * @param {boolean} [params.includeAccountAdditionalEmails=true] - When true (and not in email test mode),
 *        also sends to each matching `users` doc's `additional_email_*` / `additionalEmails` addresses,
 *        with that row's first/last name for greetings; order/cart `info` is unchanged.
 * @param {Array<{email:string,firstName?:string,lastName?:string}>} [params.accountEmailCopiesFromOrder]
 *        Optional rows snapshotted on the order at checkout (`accountAdditionalEmailCopies`) so staff can
 *        fan out without reading `users/{uid}`.
 *
 * @returns {Promise<Object|Object[]>} single response for one recipient,
 *          or an array of per-recipient { ok, status, responseBody, to } objects.
 */
export async function sendMailMergeEmail({
    to,
    subject,
    text,
    html,
    info,
    templateKey = '',
    sourcePath = 'src/pages/ScaleComponents/emailtransporter.js',
    context = {},
    bypassTestMode = false, // If true, send to actual recipients even if TEST_MODE is enabled
    includeAccountAdditionalEmails = true,
    accountEmailCopiesFromOrder = null,
    /** For `email_logs`: London Smoke & Cure outbound profile id (Cloud Run SMTP mapping). */
    senderProfileId = 'orders',
    /** Optional lowercase From address stored on `email_logs` for Contact Manager timeline. */
    fromAddressLog = null,
}) {
    // Normalize recipients, test-mode routing
    console.log(`[emailtransporter] sendMailMergeEmail called - to: "${to}", type: ${typeof to}, TEST_MODE: ${TEST_MODE}, bypassTestMode: ${bypassTestMode}, TEST_ADDRESS: "${TEST_ADDRESS}"`);
    const intendedRecipients = normalizeToArray(to);
    console.log(`[emailtransporter] intendedRecipients after normalizeToArray:`, intendedRecipients);
    // If bypassTestMode is true, always use intended recipients; otherwise respect TEST_MODE
    let finalRecipients = (TEST_MODE && !bypassTestMode)
        ? normalizeToArray(TEST_ADDRESS) // send only to test address in test mode (unless bypassed)
        : intendedRecipients;

    /** @type {Record<string, Object>} lowercase email -> merge identity overlay */
    let identityOverlayByEmail = {};
    if (!(TEST_MODE && !bypassTestMode) && includeAccountAdditionalEmails && intendedRecipients.length > 0) {
        try {
            let { expandedRecipients, identityOverlayByEmail: overlays } =
                await expandRecipientsWithLinkedAdditionalEmails(intendedRecipients);
            const copyCompany =
                (info && (info.companyDisplay ?? info.customerName ?? info.companyName)) || '';
            const merged = mergeAccountEmailCopiesFromOrderSnapshot(
                expandedRecipients,
                overlays,
                accountEmailCopiesFromOrder,
                copyCompany
            );
            finalRecipients = merged.expandedRecipients;
            identityOverlayByEmail = merged.identityOverlayByEmail;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[emailtransporter] expand additional account emails failed; sending to primary only:', e);
            finalRecipients = intendedRecipients;
            identityOverlayByEmail = {};
            try {
                const copyCompany =
                    (info && (info.companyDisplay ?? info.customerName ?? info.companyName)) || '';
                const merged = mergeAccountEmailCopiesFromOrderSnapshot(
                    intendedRecipients,
                    {},
                    accountEmailCopiesFromOrder,
                    copyCompany
                );
                finalRecipients = merged.expandedRecipients;
                identityOverlayByEmail = merged.identityOverlayByEmail;
            } catch (e2) {
                // eslint-disable-next-line no-console
                console.warn('[emailtransporter] order snapshot additional emails merge failed:', e2);
            }
        }
    }
    console.log(`[emailtransporter] finalRecipients:`, finalRecipients, `(length: ${finalRecipients.length})`);

    // If no recipients after normalization, bail early
    if (finalRecipients.length === 0) {
        console.error(`[emailtransporter] ERROR: No valid recipients! to="${to}", intendedRecipients:`, intendedRecipients, `finalRecipients:`, finalRecipients);
        throw new Error('No valid recipient email(s) provided.');
    }

    // Helper to send exactly one email, with per-recipient merge + per-recipient log
    async function sendOne(recipientEmail) {
        const queuedAt = new Date();
        
        // Fetch recipient row for this specific email (privacy-safe personalization)
        const recRow = await getRecipientDataFromLists(recipientEmail);
        const overlayKey = cleanEmail(recipientEmail).toLowerCase();
        const accountIdOverlay = identityOverlayByEmail[overlayKey];
        const recRowMerged = accountIdOverlay ? { ...(recRow || {}), ...accountIdOverlay } : recRow;
        let data = buildMergeData(info || {}, recRowMerged);
        data = applyAccountAdditionalRecipientIdentity(data, accountIdOverlay);

        // Apply placeholder merge with tracking
        const subjectResult = replacePlaceholders(subject, data);
        const textResult = replacePlaceholders(text || '', data);
        const htmlResult = replacePlaceholders(html || '', data);

        // Combine placeholder usage from all content
        const allUsed = [...new Set([...subjectResult.placeholderUsage.used, ...textResult.placeholderUsage.used, ...htmlResult.placeholderUsage.used])];
        const allMissing = [...new Set([...subjectResult.placeholderUsage.missing, ...textResult.placeholderUsage.missing, ...htmlResult.placeholderUsage.missing])];
        const totalPlaceholders = subjectResult.placeholderUsage.totalCount + textResult.placeholderUsage.totalCount + htmlResult.placeholderUsage.totalCount;

        const literalRemaining = extractRemainingMergeTokensAfterMerge(
            subjectResult.content,
            `${htmlResult.content}\n${textResult.content}`,
        );
        if (allMissing.length > 0 || literalRemaining.length > 0) {
            const uniq = Array.from(new Set([...allMissing.map((k) => `--${k}--`), ...literalRemaining]));
            const msg = `Refusing to send: unresolved mail-merge placeholders for ${recipientEmail}: ${uniq.join(', ')}`;
            console.error('[emailtransporter]', msg);
            throw new Error(msg);
        }

        // Prepare payload (single recipient only)
        const payload = {
            to: [recipientEmail],
            subject: subjectResult.content,
            text: textResult.content,
            html: htmlResult.content,
        };

        // Prepare common log base (extract orderId if present)
        const orderIdForLog =
            info?.orderId ??
            context?.orderId ??
            data?.orderId ??
            null;

        // Get list membership
        const listMembership = await getListMembershipForEmail(recipientEmail);

        const fromNorm =
            typeof fromAddressLog === 'string' && fromAddressLog.trim()
                ? fromAddressLog.trim().toLowerCase()
                : null;
        const logBase = {
            templateKey,
            sourcePath,
            to: [String(recipientEmail).trim().toLowerCase()],
            subject: subjectResult.content,
            testMode: TEST_MODE,
            endpoint: ENDPOINT,
            accountAdditionalEmailCopy: Boolean(accountIdOverlay),
            data: { orderId: orderIdForLog },
            context: { ...context, orderId: orderIdForLog },
            payloadSummary: summarizeEmailPayload(payload),
            placeholderUsage: {
                used: allUsed,
                missing: allMissing,
                totalCount: totalPlaceholders
            },
            listMembership: listMembership,
            timingStats: {
                queuedAt: queuedAt.toISOString()
            },
            direction: 'outbound',
            senderProfileId: senderProfileId || 'orders',
            ...(fromNorm ? { fromAddress: fromNorm } : {}),
        };

        try {
            const sendStartedAt = new Date();
            const res = await fetch(ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const sendCompletedAt = new Date();
            const status = res.status;
            let responseBody = null;
            const ok = res.ok;

            try {
                responseBody = await res.clone().json();
            } catch {
                try {
                    responseBody = await res.text();
                } catch {
                    responseBody = null;
                }
            }

            const durationMs = sendCompletedAt.getTime() - sendStartedAt.getTime();
            const queueWaitMs = sendStartedAt.getTime() - queuedAt.getTime();

            await logEmail({
                ...logBase,
                ok,
                status,
                responseBody,
                timingStats: {
                    ...logBase.timingStats,
                    sendStartedAt: sendStartedAt.toISOString(),
                    sendCompletedAt: sendCompletedAt.toISOString(),
                    durationMs,
                    queueWaitMs
                }
            });

            if (!ok) {
                const errText = typeof responseBody === 'string' ? responseBody : JSON.stringify(responseBody);
                throw new Error(`Mail merge email send failed (${status}) to ${recipientEmail}: ${errText}`);
            }

            return { ok: true, status, responseBody, to: recipientEmail };
        } catch (err) {
            const errorTime = new Date();
            const queueWaitMs = errorTime.getTime() - queuedAt.getTime();
            
            await logEmail({
                ...logBase,
                ok: false,
                status: 'FETCH_ERR',
                error: String(err?.message || err),
                timingStats: {
                    ...logBase.timingStats,
                    sendStartedAt: errorTime.toISOString(),
                    sendCompletedAt: errorTime.toISOString(),
                    durationMs: 0,
                    queueWaitMs
                },
                failureDetails: {
                    errorCode: 'FETCH_ERR',
                    errorMessage: String(err?.message || err),
                    retryCount: 0,
                    lastRetryAt: null
                }
            });
            return { ok: false, status: 'FETCH_ERR', error: String(err?.message || err), to: recipientEmail };
        }
    }

    // If exactly one recipient (common transactional case), keep legacy return shape:
    if (finalRecipients.length === 1) {
        const result = await sendOne(finalRecipients[0]);
        if (!result.ok) {
            throw new Error(result.error || `Failed to send to ${result.to}`);
        }
        // For backward compat, return the transporter response body directly
        return result.responseBody;
    }

    // Multiple recipients → fan-out with privacy
    const results = [];
    for (const em of finalRecipients) {
        // Send sequentially for clearer logs and transporter rate-limit friendliness
        // (Change to Promise.all if you want parallel dispatch.)
        // eslint-disable-next-line no-await-in-loop
        const r = await sendOne(em);
        results.push(r);
    }

    return results;
}

/**
 * Sales nudge: cart cutoff reminder (Template Studio)
 * Template id: email_templates/cart_cutoff_reminder
 */
export async function sendCartCutoffReminderEmail({ to, info = {}, context = {} }) {
    const preStyle = emailCartPreBlockStyleAttr();
    const cartCutoffInnerHtml =
        `<p style="margin:0 0 12px 0;">Dear --Company Name--,</p>` +
        `<p style="margin:0 0 12px 0;line-height:1.5;">You left something in your cart!</p>` +
        `<p style="margin:0 0 12px 0;line-height:1.5;">We’re now past our trade order cut-off, but if you complete your order quickly, we’ll still do our best to get it delivered this week.</p>` +
        `<p style="margin:0 0 12px 0;line-height:1.5;">Simply select the next available delivery date at checkout, and add a note if you’d like us to try sending it sooner.</p>` +
        `<p style="margin:0 0 12px 0;line-height:1.5;">If you need any help, just let us know.</p>` +
        `<hr style="border:none;border-top:1px solid #eee;margin:22px 0;" />` +
        `<h2 style="color:#444;margin:0 0 12px 0;font-size:20px;">Cart summary</h2>` +
        `<p style="margin:0 0 16px 0;"><strong>Total:</strong> £--cartTotal--</p>` +
        `--itemsHtml--` +
        emailCartCtaButtonMergeHtml() +
        `<pre style="${preStyle}">--itemsText--</pre>`;

    const fallback = {
        subject: 'You left something in your cart',
        text:
            `Dear --Company Name--,\n\n` +
            `You left something in your cart!\n\n` +
            `We’re now past our trade order cut-off, but if you complete your order quickly, we’ll still do our best to get it delivered this week.\n\n` +
            `Simply select the next available delivery date at checkout, and add a note if you’d like us to try sending it sooner.\n\n` +
            `If you need any help, just let us know.\n\n` +
            `Open your cart: --cartUrl--\n\n` +
            `Cart summary:\n` +
            `Total: £--cartTotal--\n` +
            `--itemsText--\n\n` +
            `Thank you for choosing London Smoke & Cure`,
        html: wrapTransactionalEmailHtml(cartCutoffInnerHtml),
    };

    const t = await getTemplateOrDefault(TEMPLATE_KEYS.CART_CUTOFF_REMINDER, fallback);
    const html = applyCartNudgeTemplateHtmlTransforms(t.html, info || {});

    const routed = applyCartNudgeTestRouting({
        to,
        subject: t.subject,
        text: t.text,
        html,
    });

    return sendMailMergeEmail({
        to: routed.to,
        subject: routed.subject,
        text: routed.text,
        html: routed.html,
        info: {
            ...(info || {}),
            cartUrl: (info && info.cartUrl) || getWholesaleCartUrl(),
        },
        templateKey: TEMPLATE_KEYS.CART_CUTOFF_REMINDER,
        sourcePath: 'emailtransporter:sendCartCutoffReminderEmail',
        context: {
            ...context,
            cartNudgeTestMode: routed.isTestMode,
            cartNudgeIntendedRecipient: routed.intendedRecipient,
        },
        bypassTestMode: routed.bypassTestMode,
    });
}

/**
 * Cart nudge using any Template Studio key (same merge fields as cart cutoff reminder).
 * Use for automation rules with action.kind = email_cart_template.
 *
 * @param {object} opts
 * @param {string} opts.to
 * @param {object} [opts.info]
 * @param {object} [opts.context]
 * @param {string} opts.templateKey — Firestore `email_templates/{templateKey}` document id
 */
export async function sendCartTemplatedNudgeEmail({ to, info = {}, context = {}, templateKey }) {
    const key = String(templateKey || TEMPLATE_KEYS.CART_CUTOFF_REMINDER).trim() || TEMPLATE_KEYS.CART_CUTOFF_REMINDER;
    const preStyle = emailCartPreBlockStyleAttr();
    const cartCutoffInnerHtml =
        `<p style="margin:0 0 12px 0;">Dear --Company Name--,</p>` +
        `<p style="margin:0 0 12px 0;line-height:1.5;">You left something in your cart!</p>` +
        `<p style="margin:0 0 12px 0;line-height:1.5;">We’re now past our trade order cut-off, but if you complete your order quickly, we’ll still do our best to get it delivered this week.</p>` +
        `<p style="margin:0 0 12px 0;line-height:1.5;">Simply select the next available delivery date at checkout, and add a note if you’d like us to try sending it sooner.</p>` +
        `<p style="margin:0 0 12px 0;line-height:1.5;">If you need any help, just let us know.</p>` +
        `<hr style="border:none;border-top:1px solid #eee;margin:22px 0;" />` +
        `<h2 style="color:#444;margin:0 0 12px 0;font-size:20px;">Cart summary</h2>` +
        `<p style="margin:0 0 16px 0;"><strong>Total:</strong> £--cartTotal--</p>` +
        `--itemsHtml--` +
        emailCartCtaButtonMergeHtml() +
        `<pre style="${preStyle}">--itemsText--</pre>`;

    const fallback = {
        subject: 'You left something in your cart',
        text:
            `Dear --Company Name--,\n\n` +
            `You left something in your cart!\n\n` +
            `We’re now past our trade order cut-off, but if you complete your order quickly, we’ll still do our best to get it delivered this week.\n\n` +
            `Simply select the next available delivery date at checkout, and add a note if you’d like us to try sending it sooner.\n\n` +
            `If you need any help, just let us know.\n\n` +
            `Open your cart: --cartUrl--\n\n` +
            `Cart summary:\n` +
            `Total: £--cartTotal--\n` +
            `--itemsText--\n\n` +
            `Thank you for choosing London Smoke & Cure`,
        html: wrapTransactionalEmailHtml(cartCutoffInnerHtml),
    };

    const t = await getTemplateOrDefault(key, fallback);
    const html = applyCartNudgeTemplateHtmlTransforms(t.html, info || {});

    const routed = applyCartNudgeTestRouting({
        to,
        subject: t.subject,
        text: t.text,
        html,
    });

    return sendMailMergeEmail({
        to: routed.to,
        subject: routed.subject,
        text: routed.text,
        html: routed.html,
        info: {
            ...(info || {}),
            cartUrl: (info && info.cartUrl) || getWholesaleCartUrl(),
        },
        templateKey: key,
        sourcePath: 'emailtransporter:sendCartTemplatedNudgeEmail',
        context: {
            ...context,
            cartNudgeTestMode: routed.isTestMode,
            cartNudgeIntendedRecipient: routed.intendedRecipient,
        },
        bypassTestMode: routed.bypassTestMode,
    });
}

/**
 * Resolve cart nudge email as merged for one intended recipient (no HTTP send, no email_logs).
 * Mirrors `sendCartCutoffReminderEmail` / `sendCartTemplatedNudgeEmail` template + merge path without test-mode routing.
 *
 * @param {object} opts
 * @param {string} opts.to — intended recipient email
 * @param {object} [opts.info] — merge payload (company, cart lines, etc.)
 * @param {'cart_cutoff'|'cart_template'} opts.kind
 * @param {string} [opts.templateKey] — `email_templates` doc id when kind is `cart_template`
 */
export async function buildCartNudgeEmailPreviewForDryRun({ to, info = {}, kind, templateKey }) {
    const preStyle = emailCartPreBlockStyleAttr();
    const cartCutoffInnerHtml =
        `<p style="margin:0 0 12px 0;">Dear --Company Name--,</p>` +
        `<p style="margin:0 0 12px 0;line-height:1.5;">You left something in your cart!</p>` +
        `<p style="margin:0 0 12px 0;line-height:1.5;">We’re now past our trade order cut-off, but if you complete your order quickly, we’ll still do our best to get it delivered this week.</p>` +
        `<p style="margin:0 0 12px 0;line-height:1.5;">Simply select the next available delivery date at checkout, and add a note if you’d like us to try sending it sooner.</p>` +
        `<p style="margin:0 0 12px 0;line-height:1.5;">If you need any help, just let us know.</p>` +
        `<hr style="border:none;border-top:1px solid #eee;margin:22px 0;" />` +
        `<h2 style="color:#444;margin:0 0 12px 0;font-size:20px;">Cart summary</h2>` +
        `<p style="margin:0 0 16px 0;"><strong>Total:</strong> £--cartTotal--</p>` +
        `--itemsHtml--` +
        emailCartCtaButtonMergeHtml() +
        `<pre style="${preStyle}">--itemsText--</pre>`;

    const fallback = {
        subject: 'You left something in your cart',
        text:
            `Dear --Company Name--,\n\n` +
            `You left something in your cart!\n\n` +
            `We’re now past our trade order cut-off, but if you complete your order quickly, we’ll still do our best to get it delivered this week.\n\n` +
            `Simply select the next available delivery date at checkout, and add a note if you’d like us to try sending it sooner.\n\n` +
            `If you need any help, just let us know.\n\n` +
            `Open your cart: --cartUrl--\n\n` +
            `Cart summary:\n` +
            `Total: £--cartTotal--\n` +
            `--itemsText--\n\n` +
            `Thank you for choosing London Smoke & Cure`,
        html: wrapTransactionalEmailHtml(cartCutoffInnerHtml),
    };

    const key =
        kind === 'cart_template'
            ? String(templateKey || TEMPLATE_KEYS.CART_CUTOFF_REMINDER).trim() || TEMPLATE_KEYS.CART_CUTOFF_REMINDER
            : TEMPLATE_KEYS.CART_CUTOFF_REMINDER;

    const t = await getTemplateOrDefault(key, fallback);
    const html = applyCartNudgeTemplateHtmlTransforms(t.html, info || {});

    const recipientEmail = String(to || '').trim();
    const recRow = await getRecipientDataFromLists(recipientEmail);
    const mergedInfo = {
        ...(info || {}),
        cartUrl: (info && info.cartUrl) || getWholesaleCartUrl(),
    };
    const data = buildMergeData(mergedInfo, recRow);

    const subj = replacePlaceholders(t.subject || '', data);
    const txt = replacePlaceholders(t.text || '', data);
    const htm = replacePlaceholders(html, data);

    const used = [
        ...new Set([
            ...subj.placeholderUsage.used,
            ...txt.placeholderUsage.used,
            ...htm.placeholderUsage.used,
        ]),
    ];
    const missing = [
        ...new Set([
            ...subj.placeholderUsage.missing,
            ...txt.placeholderUsage.missing,
            ...htm.placeholderUsage.missing,
        ]),
    ];

    return {
        intendedTo: recipientEmail,
        templateKey: key,
        subject: subj.content,
        text: txt.content,
        html: htm.content,
        placeholderUsage: {
            used,
            missing,
            totalCount: used.length + missing.length,
        },
    };
}

/* ────────────────────────── Transactional Senders ────────────────────────── */

// ORDER DELIVERED
export async function sendOrderDeliveredEmail(info) {
    const to = TEST_MODE ? TEST_ADDRESS : info.customerEmail;

    const loc = info?.location && typeof info.location === 'object' ? info.location : {};
    const deliveredAt =
        info?.deliveredAt ||
        new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
    const merge = {
        ...info,
        orderId: String(info?.orderId ?? '').trim(),
        deliveredAt,
        photoURL: String(info?.photoURL || '').trim() || LOGO_URL,
        signatureURL: String(info?.signatureURL || '').trim() || LOGO_URL,
        locationLat: String(loc.lat ?? info?.locationLat ?? '').trim() || '0',
        locationLng: String(loc.lng ?? info?.locationLng ?? '').trim() || '0',
    };

    const fallback = {
        subject: `Your order --orderId-- has been delivered!`,
        text: `Hi --First Name-- --Last Name--,

Your order --orderId-- was delivered on --deliveredAt--.
--photoURL--
--signatureURL--
--locationLat--, --locationLng--

Thank you for choosing London Smoke & Cure!`,
        html: `<div style="font-family: Arial, sans-serif; color: #222;">
  <div style="text-align: center; margin-bottom: 24px;">
    <img src="${LOGO_URL}" alt="London Smoke & Cure Logo" style="max-width:220px; margin:20px auto 8px; display:block;" />
  </div>
  <h2 style="color:#444; margin-top:0;">Order Delivered</h2>
  <p>Hi --First Name-- --Last Name--,</p>
  <p>We’re pleased to let you know your order <strong>#--orderId--</strong> was delivered on <em>--deliveredAt--</em>.</p>
  <div style="margin:16px 0;">
    <img src="--photoURL--" alt="Proof photo for order --orderId--" style="max-width:300px; height:auto; display:block; margin-bottom:8px;" />
  </div>
  <div style="margin:16px 0;">
    <img src="--signatureURL--" alt="Signature for order --orderId--" style="max-width:300px; height:auto; display:block; margin-bottom:8px;" />
  </div>
  <p style="margin:16px 0;">
    <a href="https://www.google.com/maps/search/?api=1&query=--locationLat--,--locationLng--" target="_blank" style="color:#3366cc;">
      View delivery location on map
    </a>
  </p>
  <p style="margin-top:24px;">Thank you for choosing <strong>London Smoke &amp; Cure</strong>!</p>
  <div style="margin-top:30px;"><small style="color:#777;">Delivered on: --deliveredAt--</small></div>
</div>`,
    };

    const t = await getTemplateOrDefault(TEMPLATE_KEYS.ORDER_DELIVERED, fallback);
    const accountEmailCopiesFromOrder = await loadAccountEmailCopiesFromOrderById(info?.orderId);
    return sendMailMergeEmail({
        to,
        subject: t.subject,
        text: t.text,
        html: t.html,
        info: merge,
        templateKey: TEMPLATE_KEYS.ORDER_DELIVERED,
        sourcePath: 'emailtransporter:sendOrderDeliveredEmail',
        context: { orderId: info?.orderId, userId: info?.userId },
        accountEmailCopiesFromOrder,
    });
}

// INVOICE AVAILABLE
export async function sendInvoiceAvailableEmail({
    orderId,
    userId,
    pdfAttentionName,
    pdfDownloadUrl,
}) {
    let customerName = pdfAttentionName || '';
    let customerEmail = '';
    try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
            const userData = userDoc.data();
            customerEmail = userData.email;
            if (!customerName) {
                customerName =
                    userData.fullName ||
                    userData.companyName ||
                    userData.username ||
                    'Customer';
            }
        }
    } catch {
        if (!customerName) customerName = 'Customer';
    }

    const to = TEST_MODE ? TEST_ADDRESS : customerEmail;

    const invoiceLink =
        pdfDownloadUrl ||
        `https://firebasestorage.googleapis.com/v0/b/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/o/invoices%2F${encodeURIComponent(
            orderId
        )}.pdf?alt=media`;

    const fallback = {
        subject: `Invoice for your order #--orderId-- is now available`,
        text: `Hi --customerName--,

Your invoice for order #--orderId-- is now available.
You can find it in your account, or download it directly using the link below:
--invoiceLink--

Thank you for choosing London Smoke & Cure!`,
        html: `<div style="font-family: Arial, sans-serif; color: #222;">
  <div style="text-align: center; margin-bottom: 24px;">
    <img src="${LOGO_URL}" alt="London Smoke & Cure Logo" style="max-width:220px; margin:20px auto 8px; display:block;" />
  </div>
  <h2 style="color:#444; margin-top:0;">Your Invoice is Ready</h2>
  <p>Hi --customerName--,</p>
  <p>Your invoice for order <strong>#--orderId--</strong> is now available.</p>
  <p>You can find it in your account, or download it directly using the button below:</p>
  <div style="margin: 18px 0;">
    <a href="--invoiceLink--" target="_blank"
       style="display:inline-block;background:#e7b114;color:#222;padding:12px 28px;border-radius:6px;font-weight:bold;font-size:17px;text-decoration:none;margin:6px 0;">
      Download Your Invoice
    </a>
  </div>
  <p style="margin-top:24px;">Thank you for choosing <strong>London Smoke &amp; Cure</strong>!</p>
  <div style="margin-top:30px;"><small style="color:#777;">Sent: --sentAt--</small></div>
</div>`,
    };

    const t = await getTemplateOrDefault(TEMPLATE_KEYS.INVOICE_AVAILABLE, fallback);
    const accountEmailCopiesFromOrder = await loadAccountEmailCopiesFromOrderById(orderId);
    return sendMailMergeEmail({
        to,
        subject: t.subject,
        text: t.text,
        html: t.html,
        info: { orderId, customerName, invoiceLink },
        templateKey: TEMPLATE_KEYS.INVOICE_AVAILABLE,
        sourcePath: 'emailtransporter:sendInvoiceAvailableEmail',
        context: { orderId, userId },
        accountEmailCopiesFromOrder,
    });
}

// ORDER PACKED
async function buildOrderPackedMerge(info) {
    const packedAt =
        info?.packedAt ||
        new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

    /** @type {Record<string, string>} */
    const merge = {
        ...info,
        packedAt,
    };

    merge.orderId = String(merge.orderId ?? info?.orderId ?? '').trim();
    merge.customerName =
        String(merge.customerName ?? info?.customerName ?? '').trim() || 'there';

    try {
        const order = await loadOrderDataForPackedEmail(info?.orderId);
        const lines = order ? normalizeOrderLinesForPackedEmail(order) : [];
        merge.shippingMethod = order
            ? getShippingMethodLabelFromOrder(order)
            : String(info?.shippingMethod || info?.shipping_method || '').trim() ||
              'As arranged for your account';
        merge.itemsHtml = buildOrderPackedItemsHtml(lines);
        merge.itemsText = buildOrderPackedItemsPlainText(lines);
        merge.chilledProductNote = buildChilledProductNoteHtml(lines);
        merge.chilledProductNoteText = buildChilledProductNotePlainText(lines);
    } catch (e) {
        console.warn('[sendOrderPackedEmail] Failed to enrich from Firestore order:', e);
        merge.shippingMethod =
            String(info?.shippingMethod || info?.shipping_method || '').trim() ||
            'As arranged for your account';
        merge.itemsHtml = buildOrderPackedItemsHtml([]);
        merge.itemsText = buildOrderPackedItemsPlainText([]);
        merge.chilledProductNote = buildChilledProductNoteHtml([]);
        merge.chilledProductNoteText = buildChilledProductNotePlainText([]);
    }

    return merge;
}

export async function sendOrderPackedEmail(info) {
    if (isPackersCustomerPackEmailsMuted()) {
        console.info(
            '[emailtransporter] order_packed muted for this pack session — not sending',
            info?.orderId || ''
        );
        return { muted: true, templateKey: TEMPLATE_KEYS.ORDER_PACKED };
    }
    const to = TEST_MODE ? TEST_ADDRESS : info.customerEmail;
    const merge = await buildOrderPackedMerge(info);

    const fallback = {
        subject: `Your order --orderId-- has been packed!`,
        text: defaultOrderPackedTemplateText(),
        html: defaultOrderPackedTemplateHtml(),
    };

    const t = await getTemplateOrDefault(TEMPLATE_KEYS.ORDER_PACKED, fallback);
    return sendMailMergeEmail({
        to,
        subject: t.subject,
        text: t.text,
        html: t.html,
        info: merge,
        templateKey: TEMPLATE_KEYS.ORDER_PACKED,
        sourcePath: 'emailtransporter:sendOrderPackedEmail',
        context: { orderId: info?.orderId, userId: info?.userId },
    });
}

/** Published `order_packed` letter for Packers bench preview. Never uses Studio draft fields. */
export async function buildOrderPackedEmailPreview(info) {
    const merge = await buildOrderPackedMerge(info);
    const fallback = {
        subject: `Your order --orderId-- has been packed!`,
        text: defaultOrderPackedTemplateText(),
        html: defaultOrderPackedTemplateHtml(),
    };
    const t = await getTemplateOrDefault(TEMPLATE_KEYS.ORDER_PACKED, fallback);
    const subject = replaceEmailPlaceholdersFully(t.subject || '', merge);
    const text = replaceEmailPlaceholdersFully(t.text || '', merge);
    const html = replaceEmailPlaceholdersFully(t.html || '', merge);
    const unresolved = extractRemainingMergeTokensAfterMerge(subject, `${html}\n${text}`);
    return { subject, text, html, unresolved, merge };
}

// ORDER SHIPPED
export async function sendOrderShippedEmail(info) {
    if (isPackersCustomerPackEmailsMuted()) {
        console.info(
            '[emailtransporter] order_shipped muted for this pack session — not sending',
            info?.orderId || ''
        );
        return { muted: true, templateKey: TEMPLATE_KEYS.ORDER_SHIPPED };
    }
    const to = TEST_MODE ? TEST_ADDRESS : info.customerEmail;

    const merge = {
        ...info,
        orderId: String(info?.orderId ?? '').trim(),
        customerName: String(info?.customerName ?? '').trim() || 'there',
        shippingMethod:
            String(info?.shippingMethod || info?.shipping_method || '').trim() ||
            'As arranged for your account',
        shippedAt:
            info?.shippedAt ||
            new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }),
    };

    const fallback = {
        subject: `Your order --orderId-- has been shipped!`,
        text: `Hi --customerName--,

Your order --orderId-- has now been shipped.
Shipping method: --shippingMethod--
Shipped on: --shippedAt--

Thank you for choosing London Smoke & Cure!`,
        html: `<div style="font-family: Arial, sans-serif; color: #222;">
  <div style="text-align: center; margin-bottom: 24px;">
    <img src="${LOGO_URL}" alt="London Smoke & Cure Logo" style="max-width:220px; margin:20px auto 8px; display:block;" />
  </div>
  <h2 style="color:#444; margin-top:0;">Order Shipped</h2>
  <p>Hi --customerName--,</p>
  <p>Your order <strong>#--orderId--</strong> has now been shipped.</p>
  <div style="margin:12px 0;">
    <strong>Shipping method:</strong> <em>--shippingMethod--</em>
  </div>
  <div style="margin:12px 0;">
    <strong>Shipped on:</strong> <em>--shippedAt--</em>
  </div>
  <p style="margin-top:24px;">Thank you for choosing <strong>London Smoke &amp; Cure</strong>!</p>
  <div style="margin-top:30px;"><small style="color:#777;">Shipped on: --shippedAt--</small></div>
</div>`,
    };

    const t = await getTemplateOrDefault(TEMPLATE_KEYS.ORDER_SHIPPED, fallback);
    const accountEmailCopiesFromOrder = await loadAccountEmailCopiesFromOrderById(info?.orderId);
    return sendMailMergeEmail({
        to,
        subject: t.subject,
        text: t.text,
        html: t.html,
        info: merge,
        templateKey: TEMPLATE_KEYS.ORDER_SHIPPED,
        sourcePath: 'emailtransporter:sendOrderShippedEmail',
        context: { orderId: info?.orderId, userId: info?.userId },
        accountEmailCopiesFromOrder,
    });
}

// ORDER CONFIRMATION
export async function sendOrderConfirmationEmail({ orderId }) {
    if (!orderId) throw new Error('orderId required');
    const orderDoc = await getDoc(doc(db, 'orders', orderId));
    if (!orderDoc.exists()) throw new Error('Order not found');
    const order = orderDoc.data();

    // Recipient
    const to = TEST_MODE
        ? TEST_ADDRESS
        : (order.email || order.customer?.email || '');

    // Build items
    const GBP = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
    const items = Array.isArray(order.order_items) ? order.order_items : [];
    const itemsRows = items
        .map((i) => {
            const price = GBP.format(Number(i.price || 0));
            const total = GBP.format(Number(i.price || 0) * (i.quantity || 0));
            return `<tr><td>${i.name}</td><td>${i.packSize || '-'}</td><td>${i.quantity}</td><td>${price}</td><td>${total}</td></tr>`;
        })
        .join('');
    const itemsText = items
        .map(
            (i) =>
                `${i.name} (${i.packSize || '-'}) x${i.quantity} = ${GBP.format(
                    Number(i.price || 0) * (i.quantity || 0)
                )}`
        )
        .join('\n');

    const subtotal = order.order_total
        ? GBP.format(Number(order.order_total))
        : GBP.format(
            items.reduce(
                (s, it) => s + Number(it.price || 0) * (it.quantity || 0),
                0
            )
        );
    const shippingDisplay =
        order.shipping_total != null
            ? Number(order.shipping_total) === 0
                ? '£0.00'
                : GBP.format(Number(order.shipping_total))
            : '£0.00';
    const orderTotal = order.order_total
        ? GBP.format(Number(order.order_total))
        : subtotal;

    const shippingHtml = order.shipping
        ? [
            `${order.shipping.first_name || ''} ${order.shipping.last_name || ''}`.trim(),
            order.shipping.address1,
            order.shipping.address2,
            order.shipping.address3,
            order.shipping.city,
            order.shipping.postal_code,
        ]
            .filter(Boolean)
            .join('<br/>')
        : '';

    // NEW: Add firstName field for merge
    const firstName = order.customer?.first_name || order.shipping?.first_name || '';

    const templateInfo = {
        customerName:
            order.customer?.company ||
            `${order.customer?.first_name || ''} ${order.customer?.last_name || ''}`.trim() ||
            'Customer',
        firstName,
        orderNumber: orderId,
        orderId, // expose under orderId too for logs
        deliveryDateDisplay: order.checkout_data?.Delivery || '',
        companyDisplay: order.customer?.company || order.customer_id || '',
        shippingAddressHtml: shippingHtml,
        shippingAddressPlain: shippingHtml.replace(/<br\/?\>/g, ', '),
        itemsRows,
        itemsText,
        subtotal,
        shippingDisplay,
        orderTotal,
        purchaseOrder: order.purchaseOrder || order.id || '—',
        orderNote:
            (order.Notes && order.Notes[0] && order.Notes[0].content) ||
            order.orderNote ||
            '—',
        sentAt: new Date().toLocaleString('en-GB'),
    };

    const fallback = {
        subject: `Order Confirmation #--orderNumber--`,
        text: `Hi --firstName--,

Thanks for your order #--orderNumber--.

Items:
--itemsText--

Total: --orderTotal--`,
        html: `<div style="font-family: Arial, sans-serif; color:#222;">
  <div style="text-align: center; margin-bottom: 24px;">
    <img src="${LOGO_URL}" alt="London Smoke & Cure Logo" style="max-width:220px; margin:20px auto 8px; display:block;" />
  </div>
  <h2 style="color:#444; margin-top:0;">Order Confirmation</h2>
  <p>Hi --firstName--,</p>
  <p>Thanks for your order <strong>#--orderNumber--</strong>.</p>
  <table width="100%" cellpadding="8" cellspacing="0" style="border-collapse:collapse;border:1px solid #eee;margin:16px 0;">
    <thead>
      <tr style="background:#fafafa;text-align:left;">
        <th align="left">Item</th>
        <th align="left">Pack</th>
        <th align="left">Qty</th>
        <th align="left">Price</th>
        <th align="left">Total</th>
      </tr>
    </thead>
    <tbody>
      --itemsRows--
    </tbody>
  </table>
  <p><strong>Subtotal:</strong> --subtotal--</p>
  <p><strong>Shipping:</strong> --shippingDisplay--</p>
  <p><strong>Order total:</strong> --orderTotal--</p>
  <div style="margin-top:30px;"><small style="color:#777;">Sent: --sentAt--</small></div>
</div>`,
    };

    const t = await getTemplateOrDefault(TEMPLATE_KEYS.ORDER_CONFIRMATION, fallback);
    const accountEmailCopiesFromOrder = Array.isArray(order.accountAdditionalEmailCopies)
        ? order.accountAdditionalEmailCopies
        : [];
    return sendMailMergeEmail({
        to,
        subject: t.subject,
        text: t.text,
        html: t.html,
        info: templateInfo,
        templateKey: TEMPLATE_KEYS.ORDER_CONFIRMATION,
        sourcePath: 'emailtransporter:sendOrderConfirmationEmail',
        context: { orderId },
        accountEmailCopiesFromOrder,
    });
}

/**
 * Welcome email for London Smoke & Cure wholesale account created from Contact Manager (promote).
 * Merge fields: list row (if found) plus `buildPromoteWelcomeMergeInfo` — e.g. --First Name--, --Company Name--,
 * --fullName--, --contactInfo--, --phone line-- (HTML, empty if no phone), --Pricing--, --role--, --billing address--, --shipping address--, --window range--,
 * --username--, --temp password--, --login url--, --account url--, --products url--, --profile extra note--
 */
export async function sendAccountWelcomePromoteEmail({ to, info = {} }) {
    const fallback = {
        subject: 'Welcome to London Smoke & Cure — your wholesale account is ready',
        text: [
            'Hello --First Name--,',
            '',
            'Your London Smoke & Cure wholesale account is active.',
            'Company: --Company Name--',
            'Contact name: --fullName--',
            '--phone text--',
            'Username: --username--',
            'Temporary password: --temp password--',
            'Sign in: --login url--',
            'Account: --account url--',
            'Browse products: --products url--',
            '',
            'Please sign in and change your password.',
            '',
            '--profile extra note--',
        ].join('\n'),
        html: `<div style="max-width:600px;margin:0 auto;font-family:Georgia,'Times New Roman',serif;background:#fff;border-radius:12px;overflow:hidden;border:1px solid #e8e8e8;">
  <div style="background:${EMAIL_LOGO_HEADER_GRADIENT};padding:28px 20px;text-align:center;">
    <img src="${LOGO_URL}" alt="London Smoke & Cure" style="max-width:200px;height:auto;display:block;margin:0 auto;" />
    <p style="color:#fff;font-size:12px;letter-spacing:1px;margin:12px 0 0 0;">London Smoke & Cure — wholesale</p>
  </div>
  <div style="padding:32px 28px;">
    <h1 style="color:#1a1a1a;font-size:24px;margin:0 0 16px 0;">Welcome, --First Name--</h1>
    <p style="color:#4a4a4a;font-size:15px;line-height:1.6;margin:0 0 16px 0;">Your account is <strong>active</strong>. Use the details below to sign in, then change your password.</p>
    <p style="margin:0 0 12px 0;font-size:14px;color:#333;"><strong>Company:</strong> --Company Name--</p>
    <p style="margin:0 0 12px 0;font-size:14px;color:#333;"><strong>Registered name:</strong> --fullName--</p>
    --phone line--
    <div style="background:#faf9f6;border:1px solid #e5e0d8;border-radius:8px;padding:16px 18px;margin:20px 0;">
      <p style="margin:0 0 8px 0;font-size:14px;color:#333;"><strong>Username:</strong> --username--</p>
      <p style="margin:0 0 8px 0;font-size:14px;color:#333;"><strong>Temporary password:</strong> --temp password--</p>
      <p style="margin:0;font-size:14px;"><a href="--login url--" style="color:#B99040;font-weight:600;">Sign in</a>
        <span style="color:#888;"> · </span>
        <a href="--account url--" style="color:#B99040;">Your account</a>
        <span style="color:#888;"> · </span>
        <a href="--products url--" style="color:#B99040;">Products</a>
      </p>
    </div>
    <p style="color:#4a4a4a;font-size:14px;line-height:1.6;margin:0 0 8px 0;">Forgot your password? Use <strong>Forgot password</strong> on the login page.</p>
    <p style="color:#4a4a4a;font-size:14px;line-height:1.6;margin:0;">Questions: <a href="mailto:digital@londonsmokeandcure.co.uk" style="color:#B99040;">digital@londonsmokeandcure.co.uk</a></p>
    --profile extra note--
  </div>
</div>`,
    };
    const t = await getTemplateOrDefault(TEMPLATE_KEYS.ACCOUNT_WELCOME_PROMOTE, fallback);
    return sendMailMergeEmail({
        to,
        subject: t.subject,
        text: t.text,
        html: t.html,
        info,
        templateKey: TEMPLATE_KEYS.ACCOUNT_WELCOME_PROMOTE,
        sourcePath: 'emailtransporter:sendAccountWelcomePromoteEmail',
        context: { flow: 'contact_promote_welcome' },
    });
}

/* Default export kept for backwards compatibility */
export default sendOrderDeliveredEmail;
